export const PLAYERS = ["black", "red"] as const;

export type Player = (typeof PLAYERS)[number];

export type Coordinate = Readonly<{
  row: number;
  column: number;
}>;

export type RemovedCell = Readonly<{
  kind: "removed";
}>;

export type FieldCell = Readonly<{
  kind: "field";
  /** Ordered from bottom to top. */
  stack: readonly Player[];
}>;

export type Cell = RemovedCell | FieldCell;

export type PassageOutcome = Readonly<{
  kind: "passage";
  winner: Player;
}>;

export type NoMovesOutcome = Readonly<{
  kind: "no-moves";
  winner: Player;
  loser: Player;
}>;

export type GameOutcome = PassageOutcome | NoMovesOutcome;

export type GameState = Readonly<{
  cells: readonly Cell[];
  activePlayer: Player;
  outcome: GameOutcome | null;
}>;

export type GameStateInput = Readonly<{
  cells: readonly Cell[];
  activePlayer: Player;
  outcome?: GameOutcome | null;
}>;

export type NormalMove = Readonly<{
  kind: "normal";
  from: Coordinate;
  to: Coordinate;
}>;

export type PassageMove = Readonly<{
  kind: "passage";
  from: Coordinate;
  /** The forward adjacent target, which may be outside the board. */
  to: Coordinate;
}>;

export type Move = NormalMove | PassageMove;

export type RemovedOrphan = Readonly<{
  position: Coordinate;
  /** The complete bottom-to-top stack removed with the field. */
  stack: readonly Player[];
}>;

export type TransitionEvent =
  | Readonly<{
      kind: "disc-moved";
      player: Player;
      from: Coordinate;
      to: Coordinate;
    }>
  | Readonly<{
      kind: "disc-exited";
      player: Player;
      from: Coordinate;
      to: Coordinate;
    }>
  | Readonly<{
      kind: "field-removed";
      position: Coordinate;
      reason: "emptied-source";
    }>
  | Readonly<{
      kind: "orphan-cleanup";
      /** Row-major presentation order; all entries were removed simultaneously. */
      fields: readonly RemovedOrphan[];
    }>
  | Readonly<{
      kind: "turn-changed";
      from: Player;
      to: Player;
    }>
  | Readonly<{
      kind: "game-ended";
      outcome: GameOutcome;
    }>;

export type Transition = Readonly<{
  state: GameState;
  events: readonly TransitionEvent[];
}>;

export function opponentOf(player: Player): Player {
  return player === "black" ? "red" : "black";
}

export function isPlayer(value: unknown): value is Player {
  return value === "black" || value === "red";
}

export function coordinate(row: number, column: number): Coordinate {
  return Object.freeze({ row, column });
}

export function removedCell(): RemovedCell {
  return Object.freeze({ kind: "removed" });
}

export function fieldCell(stack: readonly Player[] = []): FieldCell {
  return Object.freeze({ kind: "field", stack: Object.freeze([...stack]) });
}

export function freezeOutcome(outcome: GameOutcome): GameOutcome {
  return Object.freeze({ ...outcome });
}

export function freezeGameState(input: GameStateInput): GameState {
  const cells = input.cells.map((cell) =>
    cell.kind === "removed" ? removedCell() : fieldCell(cell.stack),
  );

  return Object.freeze({
    cells: Object.freeze(cells),
    activePlayer: input.activePlayer,
    outcome: input.outcome == null ? null : freezeOutcome(input.outcome),
  });
}
